import { WhoamiResponse, whoami } from "@flanksource-ui/api/services/users";
import { AuthContext, createAuthorizer } from "@flanksource-ui/context";
import ErrorPage from "@flanksource-ui/components/Errors/ErrorPage";
import { useFlanksourceUISnippet } from "@flanksource-ui/hooks/useFlanksourceUISnippet";
import FullPageSkeletonLoader from "@flanksource-ui/ui/SkeletonLoader/FullPageSkeletonLoader";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useMemo } from "react";

const backendURL = process.env.NEXT_PUBLIC_BACKEND_URL;

type Props = {
  children: React.ReactNode;
};

export default function BasicAuthContextProvider({ children }: Props) {
  const {
    data: payload,
    isLoading,
    error
  } = useQuery<WhoamiResponse["payload"], AxiosError>(
    ["user", "whoami"],
    () => whoami(),
    {
      refetchOnWindowFocus: false,
      refetchInterval: 0,
      refetchOnReconnect: false
    }
  );

  useFlanksourceUISnippet(payload?.user, {
    backendURL: backendURL
  });

  const authorizer = useMemo(
    () => (payload ? createAuthorizer(payload) : undefined),
    [payload]
  );

  if (isLoading && !payload) {
    return <FullPageSkeletonLoader />;
  }

  if (error && !payload) {
    if (error.response?.status === 401) {
      window.location.href = `/login?return_to=${window.location.pathname}${window.location.search}`;
      return <FullPageSkeletonLoader />;
    }

    return <ErrorPage error={error} />;
  }

  if (!payload) {
    return <FullPageSkeletonLoader />;
  }

  return (
    <AuthContext.Provider
      value={{
        authorizer: authorizer!,
        isLoading,
        user: payload.user,
        backendUrl: backendURL,
        access: payload.access,
        access_actions: payload.access_actions
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
